import { create } from "zustand";
import type {
  ChatMessage,
  ChatPage,
  ChatRestriction,
  ChatAnnouncement,
  RainEvent,
  RainReceived,
} from "@crashx/shared";

export type ChatNotice = { kind: "win"; data: ChatAnnouncement } | { kind: "rain"; data: RainEvent };
interface ChatState {
  epoch: number;
  messages: ChatMessage[];
  nextCursor: string | null;
  removed: string[];
  notices: ChatNotice[];
  received: RainReceived[];
  seenReceipts: string[];
  restriction: ChatRestriction;
  reset: () => void;
  message: (message: ChatMessage) => void;
  history: (page: ChatPage) => void;
  remove: (id: string) => void;
  restrict: (restriction: ChatRestriction) => void;
  notice: (notice: ChatNotice) => void;
  receive: (receipt: RainReceived) => void;
  dismiss: (id: string) => void;
}
const empty = () => ({
  messages: [],
  nextCursor: null,
  removed: [],
  notices: [],
  received: [],
  seenReceipts: [],
  restriction: { mutedUntil: 0, bannedUntil: 0 },
});
function merge(current: ChatMessage[], incoming: ChatMessage[], removed: string[]) {
  return [...new Map([...current, ...incoming].map((message) => [message.id, message])).values()]
    .filter((message) => !removed.includes(message.id))
    .sort((a, b) => a.sentAt - b.sentAt || a.id.localeCompare(b.id));
}
export const useChatStore = create<ChatState>((set) => ({
  ...empty(),
  epoch: 0,
  reset: () => set((state) => ({ ...empty(), epoch: state.epoch + 1 })),
  message: (message) =>
    set((state) =>
      state.restriction.bannedUntil > Date.now()
        ? {}
        : { messages: merge(state.messages, [message], state.removed).slice(-200) },
    ),
  // Older pages replace the newest overflow, keeping the page the reader requested visible.
  history: (page) =>
    set((state) =>
      state.restriction.bannedUntil > Date.now()
        ? {}
        : {
            messages: merge(state.messages, page.items, state.removed).slice(0, 200),
            nextCursor: page.nextCursor,
          },
    ),
  remove: (id) =>
    set((state) => ({
      messages: state.messages.filter((message) => message.id !== id),
      removed: [...state.removed, id].slice(-500),
    })),
  restrict: (restriction) =>
    set((state) => ({
      restriction,
      messages: restriction.bannedUntil > Date.now() ? [] : state.messages,
      notices: restriction.bannedUntil > Date.now() ? [] : state.notices,
    })),
  notice: (notice) =>
    set((state) =>
      state.restriction.bannedUntil > Date.now()
        ? {}
        : {
            notices: [...state.notices.filter((item) => item.data.id !== notice.data.id), notice].slice(-30),
          },
    ),
  receive: (receipt) =>
    set((state) =>
      state.seenReceipts.includes(receipt.id)
        ? {}
        : {
            received: [...state.received, receipt].slice(-3),
            seenReceipts: [...state.seenReceipts, receipt.id].slice(-200),
          },
    ),
  dismiss: (id) => set((state) => ({ received: state.received.filter((receipt) => receipt.id !== id) })),
}));
